import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Icon,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, SyntheticEvent } from "react";
import { X } from "react-feather";
import { useTranslation } from "react-i18next";
import { LoadingIndicator } from "@/components";
import { useAuth } from "@/features/auth";
import { useSettings } from "../../contexts/settings-context";

type SettingsProps = {
  settingsModalOpen: boolean;
  toggleOpenSettingsModal: () => void;
};

export const SettingsModal = ({ settingsModalOpen, toggleOpenSettingsModal }: SettingsProps) => {
  const { t } = useTranslation("coi-platform-translations");
  const { edit, settings, isFetching, isSubmitting } = useSettings();
  const { user } = useAuth();

  const onChangeNotificationSetting = useCallback(
    (event: SyntheticEvent, checked: boolean) => {
      if (!settings?.settingsData) return;

      const payload = {
        settingsData: {
          ...settings.settingsData,
          notifications: {
            enabled: checked,
          },
        },
      };
      edit(payload);
    },
    [edit, settings?.settingsData],
  );

  const isLoading = isSubmitting || isFetching;
  const canUserManageNotifications = user?.email === settings?.notificationRecipient;

  if (!settings) return null;

  return (
    <Dialog
      open={settingsModalOpen}
      sx={{ m: "auto" }}
      PaperProps={{
        sx: {
          width: "600px",
        },
      }}
      data-testid="settings-modal"
    >
      {isLoading && <LoadingIndicator position="absolute" />}
      <Box display="flex" justifyContent="space-between" alignItems="center" padding="32px 24px 16px">
        <Typography variant="h6" data-testid="settings-modal__title">
          {t("settings.modal_title")}
        </Typography>
        <Icon component="div" onClick={toggleOpenSettingsModal} sx={{ cursor: "pointer" }}>
          <X />
        </Icon>
      </Box>
      <Divider />
      <DialogContent sx={{ padding: "16px 24px 40px" }}>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between">
            <Typography>{t("settings.email_label")}</Typography>
            <Typography color="text.disabled">{settings.notificationRecipient}</Typography>
          </Box>
          <Divider />
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Tooltip
                  placement="top"
                  arrow
                  title={!canUserManageNotifications ? t("auth.rbac.permissions_restricted.tooltip") : ""}
                >
                  <span>
                    <Switch value="notifications" color="primary" disabled={isLoading || !canUserManageNotifications} />
                  </span>
                </Tooltip>
              }
              onChange={onChangeNotificationSetting}
              sx={{ display: "flex", width: "100%", margin: 0, justifyContent: "space-between" }}
              label={
                <Box component="span" display="flex" flexDirection="column" width="100%" marginLeft="0px">
                  <Typography component="span">{t("settings.cookie_alert_emails_text")}</Typography>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {t("settings.cookie_alert_emails_sub_text")}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
            />
          </FormControl>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
